import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, Rows3, Check, X, PartyPopper, Eye, FileText } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { PriorityBadge, StatusBadge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Modal } from '@/components/ui/Modal';
import { Table } from '@/components/ui/Table';
import { Textarea } from '@/components/form/Textarea';
import { FormGroup } from '@/components/form/FormGroup';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { ItemThumb } from '@/components/ui/ItemThumb';
import {
  approveMrn,
  bulkApproveMrn,
  pendingMrns,
  rejectMrn,
  type Mrn,
} from '@/api/mrn';
import { formatDate } from '@/lib/format';
import { apiError } from '@/lib/apiError';

export function HodPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [rejectTarget, setRejectTarget] = useState<Mrn | null>(null);
  const [reason, setReason] = useState('');
  const [view, setView] = useState<'card' | 'table'>('table');
  // The MRN whose items are open for review.
  const [viewing, setViewing] = useState<Mrn | null>(null);
  const docViewer = useDocViewer();

  const query = useQuery({
    queryKey: ['mrn-pending'],
    queryFn: pendingMrns,
    refetchInterval: 60_000,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['mrn-pending'] });
    qc.invalidateQueries({ queryKey: ['mrn'] });
  }

  const approveMutation = useMutation({
    mutationFn: approveMrn,
    onSuccess: (mrn) => {
      invalidate();
      toast.pushStep(`MRN ${mrn.mrnNo} approved`, {
        body: 'Next step — Inventory Check to confirm stock levels.',
        href: '/purchase/invcheck',
        nextLabel: 'Open Inventory Check',
        tag: `mrn-approved-${mrn.id}`,
      });
    },
    onError: (e) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectMrn(id, reason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setReason('');
      toast.push('MRN rejected', 'info');
    },
    onError: (e) => toast.push(apiError(e, 'Reject failed'), 'error'),
  });

  const bulkMutation = useMutation({
    mutationFn: bulkApproveMrn,
    onSuccess: (res) => {
      invalidate();
      toast.pushStep(`Bulk-approved ${res.approved} MRN${res.approved === 1 ? '' : 's'}`, {
        body: 'Next step — Inventory Check to confirm stock levels.',
        href: '/purchase/invcheck',
        nextLabel: 'Open Inventory Check',
        tag: 'mrn-bulk-approved',
      });
    },
    onError: (e) => toast.push(apiError(e, 'Bulk approve failed'), 'error'),
  });

  const data = query.data ?? [];

  return (
    <div>
      <SectionHeader
        title="HOD Approval"      />

      <div className="mb-2 flex items-center justify-end gap-1.5 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-1.5 backdrop-blur-xl">
        {/* View toggle — segmented pill matching the app-wide tab style */}
        <div className="mr-auto inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
          <button
            type="button"
            onClick={() => setView('card')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (view === 'card'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Card layout — full MRN detail per card"
          >
            <LayoutGrid size={13} /> Cards
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
              (view === 'table'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Table layout — one row per MRN"
          >
            <Rows3 size={13} /> Table
          </button>
        </div>
        {data.length > 0 && (
          <button
            type="button"
            onClick={async () => {
              if (await confirmDlg(`Approve all ${data.length} pending MRNs?`)) {
                bulkMutation.mutate(data.map((m) => m.id));
              }
            }}
            disabled={bulkMutation.isPending}
            className="inline-flex items-center gap-1 rounded-lg bg-success px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs"
          >
            <Check size={14} className="mr-1 inline-block align-[-2px]" /> Bulk Approve All ({data.length})
          </button>
        )}
      </div>

      {query.isFetching && data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
          Loading&hellip;
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          <PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No pending approvals
        </div>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-2 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-extrabold text-accent">{r.mrnNo}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    {formatDate(r.mrnDate)} · {r.department}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <PriorityBadge priority={r.priority} />
                  <StatusBadge status={r.status} />
                </div>
              </div>

              {r.subject && <div className="truncate text-xs font-semibold text-[var(--text-secondary)]" title={r.subject}>{r.subject}</div>}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <Meta label="Raised by">{r.raisedBy}</Meta>
                <Meta label="Authority">{r.approvalAuthority}</Meta>
                <Meta label="Purpose">{r.purpose}</Meta>
                <Meta label="Items">{r.items.length}</Meta>
                <Meta label="Required">{formatDate(r.requiredBy ?? '') || '—'}</Meta>
                <Meta label="SLA">
                  <SlaBadge sla={r.sla ?? null} />
                </Meta>
                {(r.refDoc || r.enqRef) && (
                  <Meta label="Ref">
                    <span className="">{r.refDoc || r.enqRef}</span>
                  </Meta>
                )}
              </div>

              <div className="mt-1 flex items-center gap-2 border-t border-[var(--border-color)] pt-2">
                <Button variant="glass" size="sm" onClick={() => setViewing(r)} title="See the items on this MRN">
                  <Eye size={13} className="mr-1 inline-block align-[-2px]" /> Items
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="flex-1"
                  onClick={() => approveMutation.mutate(r.id)}
                  disabled={approveMutation.isPending}
                >
                  <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approve
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setRejectTarget(r);
                    setReason('');
                  }}
                >
                  <X size={13} className="mr-1 inline-block align-[-2px]" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table<Mrn>
          storageKey="hod-approval"
          title="Pending Approvals"
          count={`Showing ${data.length}`}
          rows={data}
          rowKey={(r) => r.id}
          loading={query.isFetching && data.length === 0}
          empty={<><PartyPopper size={16} className="mr-1 inline-block align-[-3px]" /> No pending approvals</>}
          columns={[
            {
              key: 'mrnNo',
              header: 'MRN #',
              cell: (r) => <span className="font-extrabold text-accent">{r.mrnNo}</span>,
            },
            { key: 'date', header: 'Date', cell: (r) => formatDate(r.mrnDate) },
            { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
            { key: 'dept', header: 'Department', cell: (r) => r.department },
            { key: 'raisedBy', header: 'Raised By', cell: (r) => r.raisedBy },
            { key: 'purpose', header: 'Purpose', cell: (r) => r.purpose },
            {
              key: 'ref',
              header: 'Ref',
              cell: (r) => <span className="text-[11px] font-bold text-[#a78bfa]">{(r.refDoc || r.enqRef) ?? '—'}</span>,
            },
            {
              key: 'priority',
              header: 'Priority',
              cell: (r) => <PriorityBadge priority={r.priority} />,
            },
            { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
            {
              key: 'required',
              header: 'Required',
              cell: (r) => formatDate(r.requiredBy ?? '') || '—',
            },
            { key: 'authority', header: 'Authority', cell: (r) => r.approvalAuthority },
            {
              key: 'status',
              header: 'Status',
              cell: (r) => <StatusBadge status={r.status} />,
            },
            {
              key: 'sla',
              header: 'SLA',
              cell: (r) => <SlaBadge sla={r.sla ?? null} />,
            },
            {
              key: 'actions',
              header: '',
              cell: (r) => (
                <div className="flex flex-nowrap items-center justify-center gap-1">
                  <Button variant="glass" size="sm" className="!px-2.5" onClick={() => setViewing(r)} title={`See the items on ${r.mrnNo}`}>
                    View
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => approveMutation.mutate(r.id)}
                    disabled={approveMutation.isPending}
                    title={`Approve ${r.mrnNo}`}
                    aria-label={`Approve ${r.mrnNo}`}
                  >
                    <Check size={13} strokeWidth={3} className="mr-1 inline-block align-[-2px]" /> Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setRejectTarget(r);
                      setReason('');
                    }}
                    title={`Reject ${r.mrnNo}`}
                    aria-label={`Reject ${r.mrnNo}`}
                  >
                    <X size={13} strokeWidth={3} className="mr-1 inline-block align-[-2px]" /> Reject
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Items on the MRN — what the HOD is approving, with current stock alongside */}
      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? `${viewing.mrnNo} — ${viewing.items.length} item${viewing.items.length === 1 ? '' : 's'}` : ''}
        size="lg"
        footer={viewing && (
          <>
            <Button variant="glass" onClick={() => setViewing(null)}>Close</Button>
            <Button variant="glass" onClick={() => docViewer.open('mrn', viewing.id, viewing.mrnNo)}>
              <FileText size={14} className="mr-1 inline-block align-[-2px]" /> Document
            </Button>
            <Button variant="danger" onClick={() => { setRejectTarget(viewing); setReason(''); setViewing(null); }}>
              <X size={13} className="mr-1 inline-block align-[-2px]" /> Reject
            </Button>
            <Button
              variant="success"
              onClick={() => { approveMutation.mutate(viewing.id); setViewing(null); }}
              disabled={approveMutation.isPending}
            >
              <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approve
            </Button>
          </>
        )}
      >
        {viewing && (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] p-2.5 text-xs sm:grid-cols-4">
              <Meta label="Date">{formatDate(viewing.mrnDate)}</Meta>
              <Meta label="Department">{viewing.department}</Meta>
              <Meta label="Raised by">{viewing.raisedBy}</Meta>
              <Meta label="Priority"><PriorityBadge priority={viewing.priority} /></Meta>
              <Meta label="Purpose">{viewing.purpose}</Meta>
              <Meta label="Required">{formatDate(viewing.requiredBy ?? '') || '—'}</Meta>
              {(viewing.refDoc || viewing.enqRef) && <Meta label="Ref">{viewing.refDoc || viewing.enqRef}</Meta>}
              {viewing.subject && <Meta label="Subject">{viewing.subject}</Meta>}
            </div>
            {viewing.remarks && (
              <div className="rounded-md border border-info/30 bg-info/[0.06] px-2.5 py-1.5 text-xs">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Remarks</span>{' '}
                <span className="text-[var(--text-secondary)]">{viewing.remarks}</span>
              </div>
            )}
            <div className="divide-y divide-[var(--border-color)] rounded-md border border-[var(--border-color)]">
              {viewing.items.map((it, i) => {
                const avail = it.availableStock;
                const tone = avail == null ? 'text-[var(--text-muted)]' : avail <= 0 ? 'text-danger' : avail < Number(it.qty) ? 'text-warning' : 'text-success';
                return (
                  <div key={it.id ?? i} className="flex items-center gap-3 p-2.5">
                    <ItemThumb src={it.imageUrl} size={44} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-bold text-[var(--text-primary)]">{it.itemName}</div>
                      <div className="truncate text-[11px] text-[var(--text-muted)]">{[it.itemCode, it.barcode].filter(Boolean).join(' · ') || 'Not in item master'}</div>
                      {it.remarks && <div className="truncate text-[11px] text-[var(--text-secondary)]">“{it.remarks}”</div>}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-extrabold text-[var(--text-primary)]">{Number(it.qty)} {it.unit}</div>
                      <div className={`text-[10px] font-bold ${tone}`} title="Warehouse stock now · available after reservations">
                        {it.currentStock == null ? 'No stock record' : `Stock ${it.currentStock} · Avail ${avail}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={`Reject ${rejectTarget?.mrnNo ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason })}
              disabled={!reason.trim() || rejectMutation.isPending}
            >
              Confirm Reject
            </Button>
          </>
        }
      >
        <FormGroup label="Rejection Reason" required>
          <Textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this MRN is being rejected…"
          />
        </FormGroup>
      </Modal>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </div>
      <div className="truncate font-semibold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
